/**
 * /integrations-error — workspace-less fallback landing for OAuth
 * callback failures (#1123).
 *
 * The GitHub App `/setup` and Slack OAuth `/callback` server routes
 * normally redirect back to the user's Integrations settings tab with
 * a `?github=<code>` / `?slackError=<code>` query param the tab uses
 * to surface a context-appropriate banner. When the failure happens
 * BEFORE the state token could be peeked (e.g. the token couldn't
 * even parse, or no token at all), there is no workspaceId to scope
 * the redirect to — those branches land here instead.
 *
 * Goal: still in OttoTester chrome, still actionable. Reads the
 * `?code=<status>` query param emitted by the server, renders the
 * matching banner copy from the same code vocabulary the
 * workspace-scoped page uses, and offers a one-click CTA back to the
 * user's default workspace.
 *
 * The page lives inside `IdentityGate` (the user is authenticated to
 * have hit a callback at all) but does NOT require workspace context
 * — sibling of `/onboarding` and `/account` in the route tree.
 */
import { Link, useSearchParams } from 'react-router-dom'
import { AlertCircle } from 'lucide-react'

import { Button } from '@/components/ui'
import { AuthLayout } from '@/auth/AuthLayout'

/** Banner copy keyed by status code. Mirrors `GITHUB_STATUS_MESSAGES`
 * in `WorkspaceSettings/Integrations.tsx` (the consolidated Integrations
 * tab post-#1153 that absorbed the prior CiIntegrations page) so the
 * two surfaces show the same explanation for the same failure.
 * Unknown codes fall through to a generic message rather than
 * rendering nothing — the code itself is shown in the technical-
 * details disclosure so a support engineer can grep for it. */
const STATUS_COPY: Record<string, { headline: string; detail: string }> = {
  'error-not-configured': {
    headline: 'Integration not configured on this server',
    detail:
      "The integration isn't set up on this installation. Ask whoever runs OttoTester here to turn it on.",
  },
  'error-expired-state': {
    headline: 'Install link expired',
    detail:
      'The install link expired or was invalid. Open the integrations tab in your workspace and click Connect again to restart.',
  },
  'error-user-mismatch': {
    headline: 'Wrong account for this install',
    detail:
      'This install was started by a different account. Sign in as the account that initiated it, or restart the install from this account.',
  },
  'error-missing-permission': {
    headline: 'You no longer have permission',
    detail:
      "You no longer have permission to manage integrations for this workspace. Ask a workspace admin to retry, or check that your role hasn't changed.",
  },
  'error-installation-missing': {
    headline: "Couldn't read the install id",
    detail:
      "GitHub didn't return an installation id. Open the integrations tab and click Connect GitHub again.",
  },
  'error-unknown': {
    headline: 'Something went wrong',
    detail:
      'Something went wrong completing the install. Try again. If it keeps failing, ask whoever runs OttoTester here to check the server log.',
  },
}

const GENERIC: { headline: string; detail: string } = {
  headline: "Couldn't complete the integration",
  detail:
    'Something went wrong on the way back from the integration provider. Open your workspace and try again.',
}

export function IntegrationsError() {
  const [params] = useSearchParams()
  const code = (params.get('code') ?? '').trim()
  const { headline, detail } = STATUS_COPY[code] ?? GENERIC

  return (
    <AuthLayout title={headline}>
      <div className="flex flex-col items-center gap-4 py-2">
        <AlertCircle className="h-10 w-10 text-destructive" aria-hidden="true" />
        <p className="text-sm text-center text-muted-foreground leading-relaxed">
          {detail}
        </p>

        {/* Code shown for support context — the user can name it
            when reporting the failure. Mirrors the auth-error page's
            `<details>` pattern (#1123). Only renders when we have
            something concrete to show. */}
        {code && (
          <details className="w-full mt-2 text-xs">
            <summary className="cursor-pointer text-muted-foreground hover:text-foreground">
              Technical details (for support)
            </summary>
            <div className="mt-2 rounded-md border border-border bg-muted/30 p-3 break-words">
              <span className="font-semibold text-muted-foreground">Code: </span>
              <code className="text-foreground">{code}</code>
            </div>
          </details>
        )}

        {/* `/account` is the SPA's "land me in my default workspace"
            redirect. From there the user can navigate to Integrations
            and retry. */}
        <div className="flex gap-2 w-full mt-2">
          <Button asChild variant="default" className="flex-1">
            <Link to="/account">Go to your workspace</Link>
          </Button>
        </div>
      </div>
    </AuthLayout>
  )
}
